import React, { useState, useCallback, useMemo } from "react";
import { TutorialContext } from "./TutorialContext";
import { TutorialConfig, TutorialContextValue } from "../../types/tutorial";

interface TutorialProviderProps {
  config: TutorialConfig;
  children: React.ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({
  config,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const startTutorial = useCallback(() => {
    setCurrentStepIndex(0);
    setIsOpen(true);
  }, []);

  const closeTutorial = useCallback(() => {
    setIsOpen(false);
  }, []);

  const next = useCallback(() => {
    if (currentStepIndex < config.steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      setIsOpen(false);
    }
  }, [currentStepIndex, config.steps.length]);

  const prev = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prevIndex) => prevIndex - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((index: number) => {
    setCurrentStepIndex(index);
  }, []);

  const restart = useCallback(() => {
    setCurrentStepIndex(0);
    setIsOpen(true);
  }, []);

  const value: TutorialContextValue = useMemo(
    () => ({
      isOpen,
      currentStepIndex,
      steps: config.steps,
      pageTitle: config.pageTitle,
      startTutorial,
      closeTutorial,
      next,
      prev,
      goToStep,
      restart,
    }),
    [
      isOpen,
      currentStepIndex,
      config.steps,
      config.pageTitle,
      startTutorial,
      closeTutorial,
      next,
      prev,
      goToStep,
      restart,
    ]
  );

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export default TutorialProvider;
